import store from '@store';
import router, { route } from '@router/router';
import useEventBus from '@assets/plugins/eventBus';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { useLpUsage } from '@models/profile/useLpUsage';
import { usePtiUsage } from '@models/profile/usePtiUsage';
import { useProfileList, Profile } from '@models/profile/profileList';
import {
    useProfileInfo,
    updateLocalSid,
    Product,
    ProductMenu
} from '@models/profile/useCurrentProfile';
import { usePermission } from '@models/profile/useProfilePermission';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useExperienceList } from '@models/experience/useExperienceList';
import analyticsService from '@assets/services/analytics.service';

const { account } = useAccountInfo();
const { eventBus } = useEventBus();
const { getLpUsage } = useLpUsage();
const { getPtiUsage } = usePtiUsage();
const { experienceList, queryExperienceList } = useExperienceList();
const { hasExperienceV4Permission, getPermission } = usePermission();

const { profileList } = useProfileList();
const {
    profileInfo,
    profileCurrentProduct,
    profileProductMenuList,
    setProfileInfo,
    setProfileProductMenu,
    updateChangeProfileLoading
} = useProfileInfo();
const { getProfilePackageInfo } = usePackageInfo();
const { getProfileNpPackageInfo } = useNPPackageInfo();

/**
 * 切换档案
 * @param profileId
 * @param resultCallback
 * @param pageLoading
 * @returns
 */
export function changeProfile(
    profileId: string,
    resultCallback: (T?: Profile) => void,
    pageLoading: boolean = true
) {
    if (!profileId) {
        throw new Error('change profile sid is required.');
    }

    if (!profileList.value || !profileList.value.some(profile => profile.sid === profileId)) {
        router.push({
            name: 'ProfileList'
        });
        return;
    }

    // 更新全局loading
    pageLoading && store.commit('master/main/SET_PAGE_LOADING_STATUS', true);

    // 更新Loading状态
    updateChangeProfileLoading(true);

    // 切换档案
    setProfileInfo(profileId);

    // 更新本地
    updateLocalSid(profileId);

    if (profileInfo.value) {
        const { isNpPackage, sid } = profileInfo.value;
        const gotoProfileList = () => {
            router.push({
                name: 'ProfileList'
            });
            updateChangeProfileLoading(false);
        };
        const getPackageInfoAfter = () => {
            // 获取权限信息
            getPermission(
                () => {
                    resultCallback &&
                        typeof resultCallback === 'function' &&
                        resultCallback(profileInfo.value);
                    updateChangeProfileLoading(false);
                    account.value?.id &&
                        analyticsService.sendLastProfile(account.value?.id, {
                            last_project_id: profileInfo.value?.sid,
                            last_project_domain: profileInfo.value?.domain,
                            last_project_package: profileInfo.value?.packageType,
                            last_project_is_datain: profileInfo.value?.isActived,
                            'last_project_is_campaign4.0': Number(hasExperienceV4Permission.value)
                        });
                },
                err => {
                    getPermission(() => {
                        gotoProfileList();
                    });
                }
            );
        };

        // 获取套餐信息，新套餐也需要获取老套餐信息
        const queryList: any = [getProfilePackageInfo];
        const resultList: (0 | 1)[] = [];
        isNpPackage && queryList.push(getProfileNpPackageInfo);
        queryList.forEach((query: any) => {
            query({
                sid: profileInfo.value?.sid,
                resultCallback: () => {
                    resultList.push(1);
                    if (resultList.filter(o => o === 1).length === queryList.length)
                        getPackageInfoAfter();
                },
                rejectCallback: () => {
                    resultList.push(0);
                    getPermission(() => {
                        gotoProfileList();
                    });
                }
            });
        });

        // 获取使用量
        [
            {
                activeKey: 'ptxEnabled',
                activeFn: getLpUsage
            },
            {
                activeKey: 'ptxEnabled',
                activeFn: getPtiUsage
            }
        ].forEach(item => {
            item.activeFn?.({
                sid: profileId
            });
        });
    }
}

/**
 * 切换产品
 */
export function changeProduct(product: Product) {}

/**
 * 切换导航
 */
export function changeMenu(menu: ProductMenu) {
    if (profileCurrentProduct.value?.key === 'landing_page') {
        router.push({
            name: menu.routerName,
            params: router.currentRoute.value.params,
            query: router.currentRoute.value.query
        });
    } else {
        if (profileCurrentProduct.value?.key === 'experience') {
            if (hasExperienceV4Permission.value && menu.key === 'CAMPAIGN') {
                route.value.name !== 'ExperienceList' &&
                    router.push({
                        name: 'ExperienceList',
                        params: {
                            needQuery: 'true'
                        }
                    });
            } else {
                route.value.name !== 'ExperienceV3' &&
                    router.push({
                        name: 'ExperienceV3'
                    });
            }
        }
        eventBus.$emit('changeProfileMenuItem', menu);
    }
    setProfileProductMenu(menu);
}

/**
 * 进入Experience
 */
export function gotoExperience(
    sid: string,
    productKey: string,
    routerName: string,
    resoultCallback?: () => void
) {
    let name = routerName;
    if (productKey === 'experience') {
        setProfileProductMenu(profileProductMenuList.value.experience?.[0]);
        name = hasExperienceV4Permission.value ? 'ExperienceList' : 'ExperienceV3';
    }
    router
        .push({
            name,
            params: {
                sid,
                needQuery: name === 'ExperienceList'
            }
        })
        .then(() => {
            resoultCallback && typeof resoultCallback === 'function' && resoultCallback();
        });
}
